import React, { useReducer } from "react";
import "./pre-revenue-valuation.css";
import NavBar from "../../../../layout/navbar/navbar";
import BasicVCMethod from "./methods/basic-vc-method";
import BerkusMethod from "./methods/berkus-method";
import ScorecardMethod from "./methods/scorecard-method";
import { Accordion, Card, Container, Tabs, Tab } from "react-bootstrap";
import { FaChevronLeft, FaChevronDown, FaEllipsisH } from "react-icons/fa";
import { IoMdInformationCircleOutline } from "react-icons/io";
import { useSupabaseApp } from "../../../../SupabaseApp";

/**
 * PreRevenueValuation Component
 * 
 * Main pre-revenue valuation calculator allowing founders to evaluate company value
 * using multiple valuation methodologies. Provides method selection via useReducer
 * (Basic VC Method, Berkus Method, Scorecard Method). Displays tabbed interface with
 * accordion-based method selection and expand/collapse UI. Each method offers different
 * approaches to valuing pre-revenue ventures based on factors like team, market, and execution.
 * Calculates pre-money and post-money valuations for fundraising negotiations.
 * Requires user authentication.
 * 
 * @returns {JSX.Element} Page with pre-revenue valuation methods and calculator
 */
const PreRevenueValuation = (props) => {
  // realm function starts here
  const { currentUser, supabase } = useSupabaseApp();

  // check if user is logged in or not
  if (!currentUser) {
    throw new Error(`Must be logged in`);
  }

  // hook to display methods
  const [state, dispatch] = useReducer(reducer, {
    basicvcmethod: true,
    berkusmethod: true,
    scorecardmethod: true,
  });

  // function to update hook
  function reducer(state, action) {
    let newState;

    switch (action.type) {
      case "change basicVCMethod":
        newState = {
          basicvcmethod: !state.basicvcmethod,
          berkusmethod: state.berkusmethod,
          scorecardmethod: state.scorecardmethod,
        };
        break;

      case "change berkusMethod":
        newState = {
          basicvcmethod: state.basicvcmethod,
          berkusmethod: !state.berkusmethod,
          scorecardmethod: state.scorecardmethod,
        };
        break;

      case "change scorecardMethod":
        newState = {
          basicvcmethod: state.basicvcmethod,
          berkusmethod: state.berkusmethod,
          scorecardmethod: !state.scorecardmethod,
        };
        break;

      default:
        throw new Error();
    }

    return newState;
  }

  return (
    <>
      {/* navbar */}
      <NavBar />

      {/* pre-revenue business simulation */}
      <div id="prerevenue">
        <Container className="col-11" style={{ paddingBottom: "200px" }}>
          {/* title and info note */}
          <div id="title-div">
            <span id="title-text">
              Pre-revenue business valuation
              <sup data-tip="Estimate the valuation of your non-revenue company with industry standards and proven frameworks.">
                <IoMdInformationCircleOutline id="information-icon" />
              </sup>
            </span>
          </div>

          {/* tabs based on methods */}
          <div>
            <Tabs className="companytabs" defaultActiveKey="venturecapital">
              <Tab eventKey="venturecapital" title="Venture capital">
                <div className="tab-body">
                  <Accordion defaultActiveKey="basicvcmethod">
                    <Accordion.Item eventKey="basicvcmethod">
                      <Accordion.Header
                        className="companycardheader"
                        onClick={() =>
                          dispatch({ type: "change basicVCMethod" })
                        }
                      >
                        <div className="companyarrowicon">
                          {state.basicvcmethod ? (
                            <FaChevronDown className="arrow-icon" />
                          ) : (
                            <FaChevronLeft className="arrow-icon" />
                          )}
                        </div>

                        <div className="companytitlecard">
                          Basic venture capital method
                        </div>

                        <div className="companyellipsisicon">
                          <FaEllipsisH />
                        </div>
                      </Accordion.Header>

                      <Accordion.Body style={{ fontFamily: "AileronRegular" }}>
                        <BasicVCMethod supabase={supabase} />
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </div>
              </Tab>

              <Tab eventKey="berkus" title="Berkus">
                <div className="tab-body">
                  <Accordion defaultActiveKey="berkusmethod">
                    <Accordion.Item eventKey="berkusmethod">
                      <Accordion.Header
                        className="companycardheader"
                        onClick={() =>
                          dispatch({ type: "change berkusMethod" })
                        }
                      >
                        <div className="companyarrowicon">
                          {state.berkusmethod ? (
                            <FaChevronDown className="arrow-icon" />
                          ) : (
                            <FaChevronLeft className="arrow-icon" />
                          )}
                        </div>

                        <div className="companytitlecard">Berkus method</div>

                        <div className="companyellipsisicon">
                          <FaEllipsisH />
                        </div>
                      </Accordion.Header>

                      <Accordion.Body style={{ fontFamily: "AileronRegular" }}>
                        <BerkusMethod supabase={supabase} />
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </div>
              </Tab>

              <Tab eventKey="scorecard" title="Scorecard">
                <div className="tab-body">
                  <Accordion defaultActiveKey="scorecardmethod">
                    <Accordion.Item eventKey="scorecardmethod">
                      <Accordion.Header
                        className="companycardheader"
                        onClick={() =>
                          dispatch({ type: "change scorecardMethod" })
                        }
                      >
                        <div className="companyarrowicon">
                          {state.scorecardmethod ? (
                            <FaChevronDown className="arrow-icon" />
                          ) : (
                            <FaChevronLeft className="arrow-icon" />
                          )}
                        </div>

                        <div className="companytitlecard">Scorecard method</div>

                        <div className="companyellipsisicon">
                          <FaEllipsisH />
                        </div>
                      </Accordion.Header>

                      <Accordion.Body style={{ fontFamily: "AileronRegular" }}>
                        <ScorecardMethod supabase={supabase} />
                      </Accordion.Body>
                    </Accordion.Item>
                  </Accordion>
                </div>
              </Tab>

              {/* <Tab eventKey="riskfactorsimulation" title="Risk Factor Simulation">

                                    </Tab> */}
            </Tabs>
          </div>
        </Container>
      </div>
    </>
  );
};

export default PreRevenueValuation;
